import type {UsersSearch} from "./list-users.schema";

import {listUsers} from "@project/sdk/admin";
import {queryOptions} from "@tanstack/react-query";
import {z} from "zod";

import {queryPolicyDefaults} from "../../../../shared/query/query-policies";
import {mapPaginatedResponse} from "../../../../types/pagination";
import {userKeys} from "../../user-keys";
import {userResponseSchema} from "../get-user/get-user.schema";

import {listUsersMapper} from "./list-users.mapper";

/**
 * Query options factory for the users list.
 *
 * Usage in a route loader:
 *   queryClient.ensureQueryData(listUsersQueryOptions({ page: 1, perPage: 20 }))
 *
 * Usage in a component:
 *   const { data } = useSuspenseQuery(listUsersQueryOptions(search));
 */
export function listUsersQueryOptions(search: Partial<UsersSearch>) {
  const params = listUsersMapper.toSdk(search);

  return queryOptions({
    ...queryPolicyDefaults.warm,
    queryFn: async () => {
      const response = await listUsers(params);

      // Anti-corruption layer: ensure backend perfectly matches UI SDK types
      const validatedData = z.array(userResponseSchema).parse(response.data);

      return mapPaginatedResponse({
        data: validatedData,
        meta: response.meta,
      });
    },
    queryKey: userKeys.list(params),
  });
}
